import { MenuItem, TextField } from '@mui/material';
import { useEntities } from '@/api/entities';
import { EmployeePicker } from '@/components/EmployeePicker';
import { FYSelect } from '@/components/FYSelect';
import { StateSelect } from '@/components/StateSelect';
import { textFieldProps } from '../textFieldProps';
import type { FieldComponentProps, FieldTypeSpec, ResolvedField } from '../types';

// The app's existing pickers, behind the common field contract — no second picker of each kind.

/** These pickers spread `sx` into an object, so stacked sx layers are merged into one first. */
function plainSx(field: ResolvedField<unknown>): object | undefined {
  const sx = field.sx;
  return Array.isArray(sx) ? Object.assign({}, ...sx.filter((layer) => typeof layer === 'object')) : (sx as object | undefined);
}

export interface EmployeeConfig { entityId?: string; status?: string }

export function EmployeeInput({ field, value, onChange, error }: FieldComponentProps<string | null, EmployeeConfig>) {
  return (
    <EmployeePicker value={value ?? null} onChange={onChange} label={typeof field.label === 'string' ? field.label : undefined}
                    size={field.size} sx={plainSx(field)} entity_id={field.config.entityId} status={field.config.status}
                    disabled={field.disabled} width={field.fullWidth ? '100%' : undefined} error={!!error}
                    helperText={error ?? (typeof field.helperText === 'string' ? field.helperText : undefined)} />
  );
}

export function EntityInput({ field, value, onChange, onBlur, error }: FieldComponentProps<string | null>) {
  const entities = useEntities();
  const active = (entities.data ?? []).filter((e) => !e.is_deleted);
  return (
    <TextField {...textFieldProps(field, error)} select value={value ?? ''} onBlur={onBlur}
               onChange={(e) => onChange(e.target.value || null)}>
      {active.map((e) => <MenuItem key={e.id} value={e.id}>{e.name}</MenuItem>)}
    </TextField>
  );
}

export interface StateConfig { countryCode?: string }

export function StateInput({ field, value, onChange, error }: FieldComponentProps<string | null, StateConfig>) {
  return (
    <StateSelect countryCode={field.config.countryCode ?? 'IN'} value={value ?? null} onChange={onChange}
                 label={typeof field.label === 'string' ? field.label : undefined} size={field.size}
                 required={field.required} sx={plainSx(field)} fullWidth={field.fullWidth}
                 helperText={error ?? (typeof field.helperText === 'string' ? field.helperText : undefined)} />
  );
}

export interface FYConfig { past?: number; future?: number; allowEmpty?: boolean; emptyLabel?: string }

export function FYInput({ field, value, onChange }: FieldComponentProps<string | null, FYConfig>) {
  const { past, future, allowEmpty, emptyLabel } = field.config;
  return (
    <FYSelect value={value ?? ''} onChange={onChange} label={typeof field.label === 'string' ? field.label : undefined}
              size={field.size} sx={plainSx(field)} width={field.fullWidth ? '100%' : undefined} past={past} future={future}
              allowEmpty={allowEmpty} emptyLabel={emptyLabel} disabled={field.disabled} />
  );
}

export const pickerTypes: Record<string, FieldTypeSpec> = {
  employee: { component: EmployeeInput },
  entity: { component: EntityInput },
  state: { component: StateInput },
  fy: { component: FYInput },
};
